import { useState, type ReactNode } from 'react'
import { ChevronRight, X } from 'lucide-react'

import { pluralize } from '@/lib/pluralize'
import { formatRelativeTime } from '@/lib/relative-time'
import { describeActor } from '@/modules/audit'
import { useLoadMore } from '@/lib/use-load-more'
import { cn } from '@/lib/utils'
import { Avatar } from '@/ui/avatar'
import { Button } from '@/ui/button'
import { buttonVariants } from '@/ui/button-variants'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/ui/dialog'
import { FilterSelect } from '@/ui/filter-select'
import { Input } from '@/ui/input'
import { LoadMoreButton } from '@/ui/load-more-button'
import { RetryNotice } from '@/ui/retry-notice'
import { useAssistantBrowserCommand } from '@/lib/assistant-browser-commands'
import { assistantBrowserCommands } from '@/lib/assistant-browser-commands.generated'

import { useEntityAncestors } from '../lib/entity-queries'
import { useSettingHistoryFor } from '../lib/setting-queries'
import { settingTargetOf, type SettingTargetProps } from '../lib/setting-target'
import { propertyLabel, settingLabel, toView, type DetailRow, type EventView } from './setting-history-model'

const ALL = 'all'

const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_minmax(0,10rem)_6.5rem_1.5rem] items-center gap-3 px-5'
const COLUMN_HEAD =
  'text-muted-foreground text-[0.625rem] font-semibold tracking-wider uppercase'

const DATE_ITEMS: Record<string, string> = {
  [ALL]: 'Any date',
  '1': 'Last 24 hours',
  '7': 'Last 7 days',
  '30': 'Last 30 days',
}

const SOURCE_ITEMS: Record<string, string> = {
  [ALL]: 'All sources',
  local: 'This entity only',
  inherited: 'Inherited',
}

type SettingHistoryDialogProps = SettingTargetProps & {
  open: boolean
  onOpenChange: (open: boolean) => void
  settingLabel?: string
  description?: ReactNode
  formatValue?: (value: unknown) => string
}

export function SettingHistoryDialog(props: SettingHistoryDialogProps) {
  const { open, onOpenChange, settingLabel: customLabel, description, formatValue } = props
  const target = settingTargetOf(props)
  const filterable = target.kind === 'entity'
  const [query, setQuery] = useState('')
  const [actor, setActor] = useState<{ id: string; type: string; name: string } | null>(null)
  const [dateRange, setDateRange] = useState(ALL)
  const [from, setFrom] = useState<string | undefined>()
  const [source, setSource] = useState(ALL)
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({})
  const criteria = {
    search: query.trim() || undefined,
    actorId: actor?.id,
    actorType: actor?.type,
    from,
    source: source as 'all' | 'local' | 'inherited',
  }
  const history = useSettingHistoryFor(target, open, criteria)
  const ancestors = useEntityAncestors(
    open && target.kind !== 'resource' ? target.entityId : null,
  )
  const resolvedLabel = customLabel ?? settingLabel(target.settingKey)
  const pager = useLoadMore(history, 'change events')
  const entries = pager.items

  const views = entries.map((entry) =>
    toView(
      entry,
      target.entityId,
      (sourceEntityId) =>
        ancestors.data?.items.find((a) => a.entityId === sourceEntityId)?.name,
      (property) => propertyLabel(target.settingKey, property),
      formatValue,
    ),
  )
  const people = [...new Map(entries.map((entry) => [
    entry.actor.id,
    { id: entry.actor.id, type: entry.actor.type, name: describeActor(entry.actor) },
  ])).values()]
  const person = actor ? `actor:${actor.id}` : ALL
  const personItems: Record<string, string> = {
    [ALL]: 'All people',
    ...Object.fromEntries([...people, ...(actor ? [actor] : [])].map((candidate) => [
      `actor:${candidate.id}`,
      candidate.name,
    ])),
  }
  const anyFilter =
    query.trim() !== '' || actor !== null || dateRange !== ALL || source !== ALL

  const defaultOpenId = views[0]?.entry.id

  useAssistantBrowserCommand(
    assistantBrowserCommands.filter,
    ({ value }) => {
      setQuery(value)
      return true
    },
    ({ surface }) => open && filterable && surface === 'settings-history',
  )
  useAssistantBrowserCommand(assistantBrowserCommands.settingHistory, ({ activityId, expanded }) => {
    if (
      !open
      || (!expanded && query.trim() !== '')
      || !views.some((view) => view.entry.id === activityId && view.rows.length > 0)
    ) {
      return false
    }
    setOpenRows((current) => ({ ...current, [activityId]: expanded }))
    return true
  })

  function clearFilters() {
    setQuery('')
    setActor(null)
    setDateRange(ALL)
    setFrom(undefined)
    setSource(ALL)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col gap-0 overflow-hidden p-0">
        <div className="border-border flex items-start justify-between gap-4 border-b px-5 py-4">
          <div className="flex flex-col gap-0.5">
            <DialogTitle>Settings change history</DialogTitle>
            <DialogDescription>
              {description ??
                (target.kind === 'resource'
                  ? `Changes to ${resolvedLabel} made on this item, newest first.`
                  : `Changes affecting ${resolvedLabel} at this entity, newest first.`)}
            </DialogDescription>
          </div>
          <DialogClose
            aria-label="Close"
            className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
          >
            <X />
          </DialogClose>
        </div>

        {filterable && (
          <div className="border-border flex items-center gap-2 border-b px-5 py-3">
            <Input
              data-assistant-filter="settings-history"
              aria-label="Search change events"
              placeholder="Search changes, values, or people…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1"
            />
            <select
              aria-label="Changed by"
              className="border-input bg-input/20 focus-visible:border-ring focus-visible:ring-ring/30 text-foreground h-7 min-w-28 rounded-md border px-2 py-0.5 text-xs outline-none focus-visible:ring-2"
              value={person}
              onChange={(event) => {
                setActor(
                  people.find((candidate) => `actor:${candidate.id}` === event.target.value) ?? null,
                )
              }}
            >
              {Object.entries(personItems).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <FilterSelect
              label="Date range"
              hideLabel
              className="min-w-28"
              value={dateRange}
              onChange={(value) => {
                setDateRange(value)
                setFrom(
                  value === ALL
                    ? undefined
                    : new Date(Date.now() - Number(value) * 86_400_000).toISOString(),
                )
              }}
              items={DATE_ITEMS}
            />
            <FilterSelect
              label="Source"
              hideLabel
              className="min-w-28"
              value={source}
              onChange={setSource}
              items={SOURCE_ITEMS}
            />
            {anyFilter && (
              <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
                Clear
              </Button>
            )}
          </div>
        )}

        <div className={cn(ROW_GRID, 'bg-muted/50 border-border border-b py-2')}>
          <span className={COLUMN_HEAD}>Change</span>
          <span className={COLUMN_HEAD}>Changed by</span>
          <span className={COLUMN_HEAD}>When</span>
          <span />
        </div>

        <p className="sr-only" role="status">
          {history.isError
            ? ''
            : history.isPending
              ? 'Loading history'
              : entries.length === 0
                ? 'No changes recorded'
                : `Showing ${pluralize(entries.length, 'change')}`}
        </p>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {history.isError ? (
            <div className="px-5 py-4">
              <RetryNotice
                message="Couldn’t load the setting history."
                onRetry={() => void history.refetch()}
              />
            </div>
          ) : history.isPending ? (
            <p className="text-muted-foreground px-5 py-8 text-center">
              Loading…
            </p>
          ) : entries.length === 0 ? (
            <p className="text-muted-foreground px-5 py-8 text-center">
              {anyFilter ? 'No change events match your filters.' : 'No history yet.'}
            </p>
          ) : (
            <ol>
              {views.map((view) => {
                const id = view.entry.id
                const isOpen =
                  view.rows.length > 0 &&
                  (query.trim() !== '' || (openRows[id] ?? id === defaultOpenId))
                return (
                  <HistoryRow
                    key={id}
                    view={view}
                    isOpen={isOpen}
                    onToggle={() =>
                      setOpenRows((current) => ({ ...current, [id]: !isOpen }))
                    }
                  />
                )
              })}
            </ol>
          )}
          <LoadMoreButton pager={pager} className="mx-5 my-3" />
        </div>

        <DialogFooter className="items-center justify-between gap-4 px-5 py-3">
          <span className="text-muted-foreground">
            {anyFilter
              ? `${views.length}${history.hasNextPage ? '+' : ''} matching change ${views.length === 1 ? 'event' : 'events'}`
              : `${views.length}${history.hasNextPage ? '+' : ''} change ${views.length === 1 ? 'event' : 'events'}`}
          </span>
          <DialogClose className={buttonVariants({ size: 'sm' })}>
            Done
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function HistoryRow({
  view,
  isOpen,
  onToggle,
}: {
  view: EventView
  isOpen: boolean
  onToggle: () => void
}) {
  const { entry, title, inherited, actorName, location, rows } = view
  const firstSet = rows.length > 0 && rows.every((r) => r.before === undefined)
  const cells = (
    <>
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{title}</span>
          {inherited && (
            <span className="border-primary/20 bg-primary/10 text-primary rounded-full border px-2 py-px text-[0.625rem] font-medium">
              Inherited
            </span>
          )}
        </div>
        {rows.length > 0 && (
          <span className="text-muted-foreground">
            {firstSet
              ? 'Initial value'
              : rows.length === 1
                ? '1 change'
                : `${rows.length} changes`}
          </span>
        )}
      </div>
      <div className="flex min-w-0 items-center gap-2">
        <Avatar name={actorName} />
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium">{actorName}</span>
          {location && (
            <span className="text-muted-foreground truncate">{location}</span>
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <time
          dateTime={entry.timestamp}
          title={new Date(entry.timestamp).toLocaleString()}
          className="font-medium"
        >
          {new Date(entry.timestamp).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </time>
        <span className="text-muted-foreground">
          {formatRelativeTime(entry.timestamp)}
        </span>
      </div>
      <span className="flex justify-center">
        {rows.length > 0 && (
          <ChevronRight
            aria-hidden
            className={cn(
              'text-muted-foreground size-3.5 transition-transform',
              isOpen && 'rotate-90',
            )}
          />
        )}
      </span>
    </>
  )

  return (
    <li className="border-border border-b last:border-b-0">
      {rows.length > 0 ? (
        <button
          type="button"
          aria-expanded={isOpen}
          data-activity-id={entry.id}
          onClick={onToggle}
          className={cn(
            ROW_GRID,
            'hover:bg-muted/50 w-full py-3 text-left',
            isOpen && 'bg-muted/30',
          )}
        >
          {cells}
        </button>
      ) : (
        <div className={cn(ROW_GRID, 'py-3')}>{cells}</div>
      )}
      {isOpen && (
        <div className="flex flex-col gap-2 px-5 pb-4">
          {firstSet && (
            <p className="text-muted-foreground">
              First recorded value for this setting here, so there is no
              previous value to compare against.
            </p>
          )}
          <ChangeTable rows={rows} />
        </div>
      )}
    </li>
  )
}

function ChangeTable({ rows }: { rows: DetailRow[] }) {
  const hasPrevious = rows.some((r) => r.before !== undefined)
  return (
    <div className="border-border overflow-hidden rounded-md border">
      <table className="w-full text-xs">
        <caption className="sr-only">Changed properties</caption>
        <thead>
          <tr className="bg-muted/50 border-border border-b text-left">
            <th scope="col" className={cn(COLUMN_HEAD, 'px-3 py-1.5')}>
              Property
            </th>
            {hasPrevious && (
              <th scope="col" className={cn(COLUMN_HEAD, 'px-3 py-1.5')}>
                Previous
              </th>
            )}
            <th scope="col" className={cn(COLUMN_HEAD, 'px-3 py-1.5')}>
              {hasPrevious ? 'New' : 'Value'}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.property}
              className="border-border border-b last:border-b-0"
            >
              <td className="text-muted-foreground w-1/3 px-3 py-1.5">
                {row.property}
              </td>
              {hasPrevious && (
                <td className="w-1/3 px-3 py-1.5">
                  {row.before === undefined ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <span className="text-destructive break-all line-through">
                      {row.before}
                    </span>
                  )}
                </td>
              )}
              <td className="w-1/3 px-3 py-1.5">
                {row.after === undefined ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <span className="break-all">{row.after}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
